import { Box, Dialog, DialogContent, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { API, ElliottLearnerProfile } from '../../../../api';
import DosenInput from '../../components/DosenInput';
import DosenButton from '../../../../components/ui/Buttons/DosenButton';
import { Bone } from '../../../../components/ui/DashboardSkeletons';
import { MOBILE_SX, useIsMobileApp } from '../../../../mobile/mobileApp';

// Same limits the BE enforces (shared/utils/elliottLearnerProfile.ts).
const ROLE_MAX = 600;
const ITEM_MAX = 200;
const ITEMS_MAX = 8;

type ListKey = 'strengths' | 'growthAreas' | 'goals';

const LISTS: { key: ListKey; label: string; addLabel: string; placeholder: string }[] = [
  {
    key: 'strengths',
    label: "What you're good at",
    addLabel: 'Add a strength',
    placeholder: 'e.g. Calming upset customers'
  },
  {
    key: 'growthAreas',
    label: 'What you want to improve',
    addLabel: 'Add something to improve',
    placeholder: 'e.g. Delegating work'
  },
  {
    key: 'goals',
    label: 'Your goals',
    addLabel: 'Add a goal',
    placeholder: 'e.g. Lead a small team within a year'
  }
];

const EMPTY: ElliottLearnerProfile = { role: '', strengths: [], growthAreas: [], goals: [] };

/** What gets saved: trimmed, blank rows dropped. */
const tidy = (p: ElliottLearnerProfile): ElliottLearnerProfile => ({
  role: p.role.trim(),
  strengths: p.strengths.map((s) => s.trim()).filter(Boolean),
  growthAreas: p.growthAreas.map((s) => s.trim()).filter(Boolean),
  goals: p.goals.map((s) => s.trim()).filter(Boolean)
});

const isEmpty = (p: ElliottLearnerProfile) =>
  !p.role && !p.strengths.length && !p.growthAreas.length && !p.goals.length;

interface Props {
  open: boolean;
  onClose: () => void;
  location?: string;
}

/**
 * "What Elliott knows about me": the learner's own profile (role, strengths,
 * what they want to improve, goals) that Elliott uses to tailor every answer.
 * Built in the welcome flow; the learner can change or clear it here.
 */
export default function ElliottProfileDialog({ open, onClose, location }: Props) {
  // Phone: a form this long gets the whole screen, title and close at the top.
  const isMobileApp = useIsMobileApp();
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState<ElliottLearnerProfile>(EMPTY);
  const [draft, setDraft] = useState<ElliottLearnerProfile>(EMPTY);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const res = await API.charlieGetProfile({ location });
      const profile = { ...EMPTY, ...(res?.profile || {}) };
      setSaved(profile);
      setDraft(profile);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [location]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const dirty = useMemo(() => JSON.stringify(tidy(draft)) !== JSON.stringify(tidy(saved)), [draft, saved]);

  const setItem = (key: ListKey, index: number, value: string) =>
    setDraft((prev) => ({ ...prev, [key]: prev[key].map((item, i) => (i === index ? value : item)) }));

  const removeItem = (key: ListKey, index: number) =>
    setDraft((prev) => ({ ...prev, [key]: prev[key].filter((_, i) => i !== index) }));

  const addItem = (key: ListKey) => setDraft((prev) => ({ ...prev, [key]: [...prev[key], ''] }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await API.charlieUpdateProfile({ location, profile: tidy(draft) });
      const profile = { ...EMPTY, ...(res?.profile || tidy(draft)) };
      setSaved(profile);
      setDraft(profile);
      toast.success(
        isEmpty(profile)
          ? "Cleared. Elliott won't use any of this from your next question."
          : 'Saved. Elliott will use this from your next question.'
      );
      onClose();
    } catch {
      toast.error("Couldn't save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (saving) return;
    setDraft(saved);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobileApp}
      PaperProps={{
        sx: {
          bgcolor: '#0F0F0F',
          borderRadius: 2,
          border: '1px solid #444444',
          overflow: 'hidden',
          maxWidth: 600,
          // The theme gives every dialog paper a 10px margin under 600px, full screen ones too.
          [MOBILE_SX]: { borderRadius: 0, border: 0, maxWidth: 'none', m: 0, width: '100%' }
        }
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 2.5,
          borderBottom: '1px solid #2A2A2A',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          [MOBILE_SX]: { px: 2, py: 1, gap: 1 }
        }}
      >
        <Typography sx={{ color: '#FDFDFD', fontSize: 18, fontWeight: 700, flex: 1 }}>
          What Elliott knows about me
        </Typography>
        <IconButton onClick={handleClose} aria-label="Close" sx={{ color: '#FDFDFD' }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent
        sx={{
          bgcolor: '#0F0F0F',
          px: 3,
          py: 2.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          [MOBILE_SX]: { px: 2, gap: 2.5 }
        }}
      >
        <Typography sx={{ color: '#B6B6B6', fontSize: 14, lineHeight: '20px' }}>
          Elliott uses this to tailor every answer to you, and only you can see it. Change anything that&apos;s
          wrong or out of date.
        </Typography>

        {loading && (
          <Box aria-busy aria-label="Loading" sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, py: 2 }}>
            {Array.from({ length: 4 }, (_, i) => (
              <Bone key={i} height={44} />
            ))}
          </Box>
        )}

        {!loading && loadError && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, py: 3 }}>
            <Typography sx={{ color: 'error.main', fontSize: 14 }}>
              Couldn&apos;t load what Elliott knows about you.
            </Typography>
            <DosenButton sizeVariant="sm" tone="basic" onClick={load}>
              Try Again
            </DosenButton>
          </Box>
        )}

        {!loading && !loadError && (
          <>
            {isEmpty(saved) && (
              <Typography sx={{ color: '#FDFDFD', fontSize: 14, lineHeight: '20px' }}>
                Elliott doesn&apos;t know anything about you yet. Add your role and goals so its answers fit you.
              </Typography>
            )}

            <DosenInput
              label="Your role"
              placeholder="e.g. Customer support lead, looking after a team of six"
              multiline
              minRows={2}
              maxChars={ROLE_MAX}
              value={draft.role}
              onChange={(e: any) => setDraft((prev) => ({ ...prev, role: e.target.value }))}
            />

            {LISTS.map(({ key, label, addLabel, placeholder }) => (
              <Box key={key} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography sx={{ color: '#FDFDFD', fontSize: 14, fontWeight: 500 }}>{label}</Typography>
                {draft[key].map((item, index) => (
                  <Box key={`${key}-${index}`} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DosenInput
                      placeholder={placeholder}
                      value={item}
                      // DosenInput owns inputProps.maxLength (from maxChars, which
                      // would add a counter per row), so cap the length here.
                      InputProps={{ inputProps: { 'aria-label': `${label} ${index + 1}` } }}
                      onChange={(e: any) => setItem(key, index, e.target.value.slice(0, ITEM_MAX))}
                    />
                    <IconButton
                      onClick={() => removeItem(key, index)}
                      aria-label={`Remove ${item || 'item'}`}
                      sx={{ color: '#868686', '&:hover': { color: '#FDFDFD' } }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
                {draft[key].length < ITEMS_MAX && (
                  <Box
                    component="button"
                    type="button"
                    onClick={() => addItem(key)}
                    sx={{
                      alignSelf: 'flex-start',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      background: 'none',
                      border: 'none',
                      p: 0,
                      color: '#B6B6B6',
                      fontSize: 13,
                      fontFamily: 'Inter, sans-serif',
                      cursor: 'pointer',
                      '&:hover': { color: '#FDFDFD' },
                      [MOBILE_SX]: { minHeight: 40 }
                    }}
                  >
                    <AddIcon sx={{ fontSize: 16 }} />
                    {addLabel}
                  </Box>
                )}
              </Box>
            ))}

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                pt: 1,
                flexWrap: 'wrap'
              }}
            >
              <Box
                component="button"
                type="button"
                disabled={isEmpty(tidy(draft))}
                onClick={() => setDraft(EMPTY)}
                sx={{
                  background: 'none',
                  border: 'none',
                  p: 0,
                  color: '#868686',
                  fontSize: 13,
                  fontFamily: 'Inter, sans-serif',
                  cursor: 'pointer',
                  '&:hover:not(:disabled)': { color: '#F44336' },
                  '&:disabled': { cursor: 'default', opacity: 0.5 },
                  [MOBILE_SX]: { minHeight: 40 }
                }}
              >
                Clear Everything
              </Box>
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <DosenButton sizeVariant="sm" tone="basic" onClick={handleClose} disabled={saving}>
                  Cancel
                </DosenButton>
                <DosenButton
                  sizeVariant="sm"
                  onClick={handleSave}
                  disabled={!dirty}
                  loading={saving}
                  loadingText="Saving..."
                >
                  Save
                </DosenButton>
              </Box>
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
